import React from 'react';
import i18n from 'i18next';
import querystring from 'query-string';
import { css } from '@emotion/core';
import Typography from '@material-ui/core/Typography';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import ShareIcon from '@material-ui/icons/Share';
import PlayCircleFilledWhiteIcon from '@material-ui/icons/PlayCircleFilledWhite';

import { functional } from 'Mui/styles/color';
import { lightenDarkenColor } from 'utils/colorConvert';
import { utcDTStr2LocalDTStr } from 'utils/dateTime';
import { prefixChar } from 'utils/manipulateString';
import { convertProxyImgUrl } from 'client/utils/getProxyImgUrl';

import {
    usePrimaryPromoSelector,
    useSecondaryPromoSelector
} from 'client/game_photoContest/hooks/useReduxSelector';
import useHistoryPathPush from 'client/hooks/useHistoryPathPush';

import Button from 'Mui/Button';
import NativeShareWrapper from 'client/components/Result/Share/NativeShareWrapper';
import ShareDialogWrapper from 'ugc/components/ShareDialogWrapper';
import { gameTypes } from 'config/promoGameTypes';
import ugcTypes from 'config/ugcTypes';

function ResultContest({ isExpand, isMultiple, serialNo, resultInfo = [] }) {
    const history = useHistoryPathPush();
    const { id: contestPromoId, name: promoTitle } = usePrimaryPromoSelector(s => s.promo);
    const { type: secondaryGameType } = useSecondaryPromoSelector(s => s.promo);

    const isVideoTypeMedia = secondaryGameType === gameTypes.VIDEO_UPLOAD;

    function toPortfolio(serialNo) {
        history.push({ pathname: `/${contestPromoId}/${serialNo}` });
    }
    function getPortfolioShareUrl(serialNo) {
        const qs = querystring.stringify({ t: Date.now() });
        return `${window.location.origin}/ugc/${contestPromoId}/${serialNo}?${qs}`;
    }

    const filteredResultInfo = resultInfo.map(ri => ({
        ...ri,
        mediaLibrary: {
            ...ri?.mediaLibrary,
            media: ri?.mediaLibrary?.media?.filter(_ => _.type === ugcTypes.IMAGE)
        }
    }));

    return (
        <>
            <div css={containerStyle}>
                <Typography variant="subtitle1">
                    {i18n.t('label.serial_number')}
                    {serialNo}
                </Typography>
                {isMultiple && <ArrowDropDownIcon />}
            </div>
            {filteredResultInfo.length > 0 && (
                <Collapse in={isExpand}>
                    {filteredResultInfo.map(r => {
                        const shareUrl = getPortfolioShareUrl(r.mediaLibrary.entryId);
                        return (
                            <div css={mediaWrapperStyle} key={r.voteId}>
                                <div
                                    css={mediaImgStyle(
                                        convertProxyImgUrl({
                                            url: r.mediaLibrary?.media[0]?.result ?? '',
                                            resizeWidth: 700
                                        })
                                    )}
                                >
                                    {isVideoTypeMedia && (
                                        <PlayCircleFilledWhiteIcon css={playIconStyle} />
                                    )}
                                </div>
                                <div css={mediaInfoStyle}>
                                    <div>
                                        <Typography variant="subtitle1">
                                            {`${i18n.t(
                                                'label.contest_ugc.result.vote_id'
                                            )}${prefixChar(r.voteId, '0', 7)}`}
                                        </Typography>
                                        <Typography
                                            variant="subtitle1"
                                            css={css(`color: #757575;`)}
                                        >
                                            {`${i18n.t(
                                                'label.contest_ugc.result.vote_date'
                                            )}${utcDTStr2LocalDTStr(
                                                r.voteCreatedAt,
                                                'YYYY-MM-DD'
                                            )}`}
                                        </Typography>
                                    </div>
                                    <div>
                                        <Button
                                            css={[btnStyle, containedBtnStyle]}
                                            variant="contained"
                                            onClick={() => toPortfolio(r.mediaLibrary.entryId)}
                                        >
                                            {i18n.t('button.go_to_work_page')}
                                        </Button>
                                        <NativeShareWrapper title={promoTitle} url={shareUrl}>
                                            <ShareDialogWrapper url={shareUrl}>
                                                {({ onOpen }) => (
                                                    <Button
                                                        css={[btnStyle, outlinedBtnStyle]}
                                                        variant="outlined"
                                                        onClick={onOpen}
                                                        startIcon={<ShareIcon />}
                                                    >
                                                        {i18n.t('label.share_media_work')}
                                                    </Button>
                                                )}
                                            </ShareDialogWrapper>
                                        </NativeShareWrapper>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </Collapse>
            )}
            {isMultiple && <Divider />}
        </>
    );
}

export default ResultContest;

const containerStyle = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
`;

const mediaWrapperStyle = css`
    position: relative;
    display: flex;
    padding: 8px 0;
    &:last-of-type {
        padding-bottom: 16px;
    }
`;

const mediaImgStyle = url => css`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 150px;
    min-width: 150px;
    max-width: 150px;
    overflow: hidden;
    background-image: url(${url});
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
`;

const mediaInfoStyle = css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
    padding: 0 8px;
    > div:first-of-type {
        display: flex;
        flex-direction: column;
    }
    > div:last-of-type {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
    }
`;

const btnStyle = css`
    padding: 8px 16px;
    min-width: 120px;
    border-radius: 28px;
    white-space: nowrap;
`;

const containedBtnStyle = css`
    margin-bottom: 8px;
    background-color: ${functional.color};
    color: #fff;
    &:hover {
        background-color: ${lightenDarkenColor(functional.color, 0.1)};
    }
`;

const outlinedBtnStyle = css`
    border-color: inherit;
`;

const playIconStyle = css`
    max-width: 53px;
    max-height: 53px;
    color: #ffffff;
    width: 100%;
    height: 100%;
`;
